import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Mail, Lock, Eye, EyeOff, Fingerprint } from 'lucide-react';

// Use dynamic API URL that works on both dev and production
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname.includes('preview.emergentagent.com')) {
    return process.env.REACT_APP_BACKEND_URL || '';
  }
  return window.location.origin;
};
const API = `${getApiUrl()}/api`;

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);

  // Check for biometric availability on mount
  useEffect(() => {
    checkBiometricAvailability();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      // Check if WebAuthn is supported
      if (!window.PublicKeyCredential) return;
      
      // Check if user has stored credential
      const storedCredential = localStorage.getItem('biometric_credential_id');
      if (!storedCredential) return;
      
      // Check if platform authenticator is available
      const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
      setBiometricAvailable(available);
    } catch (e) {
      console.log('Biometric check failed:', e);
    }
  };

  const handleBiometricLogin = async () => {
    setBiometricLoading(true);
    try {
      const storedCredentialId = localStorage.getItem('biometric_credential_id');
      if (!storedCredentialId) {
        toast.error('No biometric credentials found. Please login with Google first, then set up biometrics.');
        setBiometricLoading(false);
        return;
      }

      // First verify the credential still exists in backend
      try {
        // Create challenge
        const challenge = new Uint8Array(32);
        window.crypto.getRandomValues(challenge);

        // Request biometric authentication from device
        const publicKeyCredentialRequestOptions = {
          challenge: challenge,
          allowCredentials: [{
            id: Uint8Array.from(atob(storedCredentialId.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0)),
            type: 'public-key',
            transports: ['internal', 'hybrid']
          }],
          userVerification: "required",
          timeout: 60000
        };

        const assertion = await navigator.credentials.get({
          publicKey: publicKeyCredentialRequestOptions
        });

        if (assertion) {
          // Verify with backend and get user
          const response = await axios.post(
            `${API}/auth/biometric/login?credential_id=${encodeURIComponent(storedCredentialId)}`
          );
          
          toast.success('Biometric login successful! 🔐');
          onLogin(response.data.user);
          navigate('/dashboard');
        }
      } catch (backendError) {
        // If credential not found in backend, clear local storage
        if (backendError.response?.status === 404) {
          localStorage.removeItem('biometric_credential_id');
          setBiometricAvailable(false);
          toast.error('Biometric credential expired. Please login with Google and set up biometrics again.');
        } else {
          throw backendError;
        }
      }
    } catch (error) {
      console.error('Biometric login error:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Biometric authentication cancelled');
      } else if (error.name === 'InvalidStateError') {
        // Credential doesn't match device
        localStorage.removeItem('biometric_credential_id');
        setBiometricAvailable(false);
        toast.error('Biometric not recognized. Please set up biometrics again.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Biometric login failed. Please try another method.');
      }
    } finally {
      setBiometricLoading(false);
    }
  };

  // Handle Google OAuth callback
  useEffect(() => {
    const code = searchParams.get('code');
    if (code) {
      handleGoogleCallback(code);
    }
  }, [searchParams]);

  const handleGoogleCallback = async (code) => {
    setGoogleLoading(true);
    try {
      const response = await axios.post(`${API}/auth/google/callback?code=${code}`);
      toast.success('Login successful! 🎉');
      onLogin(response.data.user);
      
      // Check for pending join
      const pendingJoin = localStorage.getItem('pendingJoin');
      if (pendingJoin) {
        try {
          const { groupId, inviteCode } = JSON.parse(pendingJoin);
          localStorage.removeItem('pendingJoin');
          await axios.post(`${API}/groups/${groupId}/join?user_id=${response.data.user.id}&invite_code=${inviteCode}`);
          navigate(`/group/${groupId}`);
          return;
        } catch (e) {}
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google login failed');
      // Clear the code from URL
      window.history.replaceState({}, document.title, '/login');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Login successful! 🎉');
      onLogin(response.data.user);
      
      const pendingJoin = localStorage.getItem('pendingJoin');
      if (pendingJoin) {
        try {
          const { groupId, inviteCode } = JSON.parse(pendingJoin);
          localStorage.removeItem('pendingJoin');
          await axios.post(`${API}/groups/${groupId}/join?user_id=${response.data.user.id}&invite_code=${inviteCode}`);
          navigate(`/group/${groupId}`);
          return;
        } catch (e) {}
      }
      
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while processing Google callback
  if (googleLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 border-4 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Signing in with Google...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* SEO */}
      <Helmet>
        <title>Login | SharePay India - Access Your Group Savings Account</title>
        <meta name="description" content="Sign in to SharePay India to manage group savings, split bills via UPI, and track shared expenses with friends and family." />
        <link rel="canonical" href="https://sharepay.today/login" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="max-w-md mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
        </Button>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-3xl p-8 shadow-xl border border-border"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold gradient-text mb-2">SharePay</h1>
            <p className="text-muted-foreground">Welcome back! Sign in to continue.</p>
          </div>

          {/* Google Sign In Button */}
          <Button
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full rounded-full py-6 font-semibold mb-4 flex items-center justify-center gap-3"
            disabled={googleLoading}
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          {/* Biometric Login Button - Only show if available */}
          {biometricAvailable && (
            <Button
              onClick={handleBiometricLogin}
              variant="outline"
              className="w-full rounded-full py-6 font-semibold mb-4 flex items-center justify-center gap-3 border-violet-300 hover:border-violet-500 hover:bg-violet-50 dark:hover:bg-violet-950/30"
              disabled={biometricLoading}
              data-testid="biometric-login-btn"
            >
              <Fingerprint className="w-5 h-5 text-violet-600" />
              {biometricLoading ? 'Verifying...' : 'Login with Fingerprint / Face ID'}
            </Button>
          )}

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 rounded-xl"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10 rounded-xl"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-violet-600 hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full py-6 font-semibold gradient-primary"
              disabled={loading}
              data-testid="login-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="text-violet-600 font-semibold hover:underline"
                data-testid="signup-link"
              >
                Sign Up
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Login;
